import React, {
  useState,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaTools,
  FaRecycle,
} from "react-icons/fa";

import {
  Recycle,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const UnderMaintenanceScrapReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(
    ThemeContext
  );

  const currentSkin =
    getCurrentSkin(
      skin
    );

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const branchId =
    localStorage.getItem(
      "branchId"
    ) || "8";

  const branchType =
    (
      localStorage.getItem(
        "branchType"
      ) || "HO"
    ).toUpperCase();

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  const loginUserId =
    localStorage.getItem(
      "loginUserId"
    ) ||
    userId ||
    "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          branchId,

        "x-branch":
          branchType,

        "x-userid":
          userId,

        "x-loginuserid":
          loginUserId,
      }),
      [
        finYearId,
        finYear,
        branchId,
        branchType,
        userId,
        loginUserId,
      ]
    );

  /* =========================================================
     FORM STATE
  ========================================================= */

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    itemCode,
    setItemCode,
  ] = useState("");

  const [
    itemName,
    setItemName,
  ] = useState("");

  /* =========================================================
     DATA
  ========================================================= */

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     TEMPLATE DESIGN CLASSES
     SAME STYLE AS FIRST PAGE
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     DATE FORMAT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const formatDate = (
    dateValue
  ) => {
    if (!dateValue) {
      return "-";
    }

    if (
      typeof dateValue ===
        "string" &&
      dateValue.includes("/")
    ) {
      return dateValue;
    }

    const date =
      new Date(
        dateValue
      );

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return dateValue;
    }

    return date.toLocaleDateString(
      "en-IN"
    );
  };

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch =
    async () => {
      if (
        !fromDate ||
        !toDate
      ) {
        toast.warning(
          "Please select both From Date and To Date"
        );

        return;
      }

      setLoading(
        true
      );

      const params = {
        branchid:
          branchId,

        fromDate:
          fromDate,

        todate:
          toDate,

        itemcode:
          itemCode || "",

        itemname:
          itemName || "",
      };

      try {
        const res =
          await axios.get(
            "https://nissi-india.com/api/underMaintenanceScrap_rpt",
            {
              params,
              headers:
                commonHeaders,
            }
          );

        const list =
          Array.isArray(
            res.data
          )
            ? res.data
            : Array.isArray(
                res.data?.data
              )
            ? res.data.data
            : [];

        setData(
          list
        );

        toast.success(
          `Loaded ${list.length} records`
        );
      } catch (err) {
        console.error(
          "Maintenance Scrap fetch failed",
          err
        );

        toast.error(
          "Failed to fetch maintenance scrap listing"
        );
      } finally {
        setLoading(
          false
        );
      }
    };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters =
    () => {
      setFromDate("");

      setToDate("");

      setItemCode("");

      setItemName("");

      setData([]);
    };

  /* =========================================================
     EXPORT CSV
     ORIGINAL WORKING FUNCTIONALITY PRESERVED
  ========================================================= */

  const handleExportCSV =
    () => {
      if (
        !data.length
      ) {
        toast.warning(
          "No data available to export"
        );

        return;
      }

      const headers = [
        "S.No",
        "Item Code",
        "Item Name",
        "UOM",
        "Removed From",
        "Removed Date",
        "DC Date",
        "Remarks",
        "Qty",
      ];

      const rows =
        data.map(
          (
            item,
            index
          ) => [
            index + 1,

            item
              .fld_itemcode ||
              "",

            item
              .fld_itemname ||
              "",

            item
              .fld_uomcode ||
              "",

            item
              .fld_removedfrom ||
              "",

            item
              .fld_removeddate ||
              "",

            item
              .fld_dcdate ||
              "",

            item
              .fld_remarks ||
              "",

            item.fld_qty ||
              0,
          ]
        );

      const csvContent =
        [
          headers,
          ...rows,
        ]
          .map((row) =>
            row
              .map(
                (cell) =>
                  `"${String(
                    cell ?? ""
                  ).replace(
                    /"/g,
                    '""'
                  )}"`
              )
              .join(",")
          )
          .join("\n");

      const blob =
        new Blob(
          [csvContent],
          {
            type: "text/csv;charset=utf-8;",
          }
        );

      const url =
        window.URL.createObjectURL(
          blob
        );

      const link =
        document.createElement(
          "a"
        );

      link.href =
        url;

      link.setAttribute(
        "download",
        "under_maintenance_scrap_report.csv"
      );

      document.body.appendChild(
        link
      );

      link.click();

      document.body.removeChild(
        link
      );

      window.URL.revokeObjectURL(
        url
      );

      toast.success(
        "CSV exported successfully"
      );
    };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Under Maintenance Scrap Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              CARD HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <Recycle className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Maintenance Scrap Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Track and manage under maintenance scrap items
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER
                SAME POSITION AS FIRST PAGE
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT
                  REAL CSV EXPORT PRESERVED
              */}

              <button
                type="button"
                onClick={
                  handleExportCSV
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaDownload
                  size={14}
                />

                Export
              </button>

              {/* FILTER */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTER FORM
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
                  SAME VISUAL STYLE AS FIRST PAGE
              ============================================= */}

              <div
                className="
                  grid
                  grid-cols-1
                  md:grid-cols-2
                  lg:grid-cols-3
                  gap-5
                "
              >

                {/* FROM DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(
                      e
                    ) =>
                      setFromDate(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                    required
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(
                      e
                    ) =>
                      setToDate(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                    required
                  />
                </div>

                {/* ITEM CODE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Code
                  </label>

                  <input
                    type="text"
                    value={
                      itemCode
                    }
                    onChange={(
                      e
                    ) =>
                      setItemCode(
                        e.target
                          .value
                      )
                    }
                    placeholder="Enter item code"
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* ITEM NAME */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Name
                  </label>

                  <input
                    type="text"
                    value={
                      itemName
                    }
                    onChange={(
                      e
                    ) =>
                      setItemName(
                        e.target
                          .value
                      )
                    }
                    placeholder="Enter item name"
                    className={
                      inputClass
                    }
                    onKeyDown={(
                      e
                    ) => {
                      if (
                        e.key ===
                        "Enter"
                      ) {
                        handleSearch();
                      }
                    }}
                  />
                </div>
              </div>

              {/* =============================================
                  ACTION BUTTONS
              ============================================= */}

              <div
                className="
                  flex
                  flex-wrap
                  justify-end
                  gap-3
                  mt-6
                  pt-5
                  border-t
                  border-slate-200
                  dark:border-slate-700
                "
              >

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span
                      className="
                        w-4
                        h-4
                        border-2
                        border-white
                        border-t-transparent
                        rounded-full
                        animate-spin
                      "
                    />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Searching..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div
            className="
              p-6
              border-b
              border-gray-200
              dark:border-gray-700
            "
          >

            <div
              className="
                flex
                flex-col
                sm:flex-row
                items-start
                sm:items-center
                justify-between
                gap-4
              "
            >

              {/* LEFT */}

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Maintenance Scrap Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} records
                </span>
              </div>

              {/* LAST UPDATED */}

              {data.length >
                0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div
              className="
                flex
                flex-col
                items-center
                justify-center
                py-16
              "
            >

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading maintenance scrap records...
              </p>
            </div>
          ) : data.length >
            0 ? (

            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                {/* HEADER */}

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Item Code
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Item Name
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      UOM
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Removed From
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Removed Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      DC Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Remarks
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Qty
                    </th>
                  </tr>
                </thead>

                {/* BODY */}

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {data.map(
                    (
                      item,
                      index
                    ) => (
                      <tr
                        key={`${item.fld_itemcode}-${index}`}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >

                        {/* S.NO */}

                        <td className="px-4 py-3 font-medium">
                          {index + 1}
                        </td>

                        {/* ITEM CODE */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.fld_itemcode ||
                              "-"}
                          </span>
                        </td>

                        {/* ITEM NAME */}

                        <td className="px-4 py-3 font-medium">
                          {item.fld_itemname ||
                            "-"}
                        </td>

                        {/* UOM */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {item.fld_uomcode ||
                            "-"}
                        </td>

                        {/* REMOVED FROM */}

                        <td className="px-4 py-3">
                          {item.fld_removedfrom ||
                            "-"}
                        </td>

                        {/* REMOVED DATE */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatDate(
                            item.fld_removeddate
                          )}
                        </td>

                        {/* DC DATE */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatDate(
                            item.fld_dcdate
                          )}
                        </td>

                        {/* REMARKS */}

                        <td className="px-4 py-3">
                          {item.fld_remarks ||
                            "-"}
                        </td>

                        {/* QTY */}

                        <td
                          className={`px-4 py-3 whitespace-nowrap text-right font-semibold ${
                            isDark
                              ? "text-green-300"
                              : "text-green-700"
                          }`}
                        >
                          {item.fld_qty ??
                            0}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <div className="flex justify-center">

                <FaTools
                  className={`h-12 w-12 mr-4 ${
                    isDark
                      ? "text-gray-500"
                      : "text-gray-400"
                  }`}
                />

                <FaRecycle
                  className={`h-12 w-12 ${
                    isDark
                      ? "text-gray-500"
                      : "text-gray-400"
                  }`}
                />
              </div>

              <h3
                className={`mt-3 text-sm font-semibold ${
                  isDark
                    ? "text-gray-300"
                    : "text-gray-700"
                }`}
              >
                No maintenance scrap records
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-gray-500"
                    : "text-gray-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default UnderMaintenanceScrapReport;